import React, { useState, useEffect, useContext } from 'react';
import { 
  MessageSquare, Plus, ThumbsUp, ThumbsDown, Check, 
  Filter, Search, AlertCircle, ChevronRight, ChevronDown,
  Edit, Trash2, Flag, Share2
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import QuestionForm from '../../components/questions/QuestionForm';
import QuestionItem from '../../components/questions/QuestionItem';

const SectionQuestionsTab = ({ courseId, sectionId, courseName, courseCode, semester, year, professorName }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [sort, setSort] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasMore: false
  });
  
  const fetchQuestions = async (page = 1, sortOrder = sort) => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/course-sections/${sectionId}/questions?page=${page}&sort=${sortOrder}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch questions');
      }
      
      const data = await response.json();
      setQuestions(data.questions);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError(error.message);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (sectionId) {
      fetchQuestions(1, sort);
    }
  }, [sectionId, sort]);
  
  const handleAskQuestion = () => {
    setShowQuestionForm(true);
  };
  
  const handleQuestionSubmit = async (questionData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...questionData,
          courseId,
          courseSectionId: sectionId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create question');
      }
      
      const newQuestion = await response.json();
      
      // Add new question to the list
      setQuestions([newQuestion, ...questions]);
      setShowQuestionForm(false);
      toast.success('Question posted successfully!');
    } catch (error) {
      console.error('Error creating question:', error);
      toast.error('Failed to post question');
    }
  };
  
  const handleVote = async (questionId, voteType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ voteType })
      });
      
      if (!response.ok) {
        throw new Error('Failed to vote');
      }
      
      const updatedQuestion = await response.json();
      
      // Update the question in the list
      setQuestions(questions.map(question => 
        question.id === questionId ? { ...question, ...updatedQuestion } : question
      ));
      
      toast.success(`${voteType === 'upvote' ? 'Upvoted' : 'Downvoted'} question`);
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote on question');
    }
  };
  
  const handleDeleteQuestion = async (questionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete question');
      }
      
      // Remove question from list
      setQuestions(questions.filter(question => question.id !== questionId));
      toast.success('Question deleted successfully');
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };
  
  const handleLoadMore = () => {
    if (pagination.hasMore) {
      fetchQuestions(pagination.page + 1, sort);
    }
  };
  
  const handleSortChange = (e) => {
    setSort(e.target.value);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality
    toast.info('Search functionality coming soon!');
  };
  
  const filteredQuestions = searchQuery 
    ? questions.filter(question => 
        question.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        question.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : questions;
  
  if (loading && pagination.page === 1) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-6 h-6 border-t-2 border-b-2 border-red-500 rounded-full animate-spin mr-2"></div>
        <span className="text-gray-600 dark:text-gray-300">Loading questions...</span>
      </div>
    );
  }
  
  if (error && pagination.page === 1) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Error Loading Questions
        </h2>
        <p className="text-gray-600 dark:text-gray-300 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => fetchQuestions(1, sort)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Section Questions
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {courseName} ({courseCode}) - {semester} {year} with {professorName}
          </p>
        </div>
        
        <div className="mt-4 md:mt-0 flex space-x-2">
          <button
            onClick={handleAskQuestion}
            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <Plus className="mr-1 -ml-1 h-4 w-4" />
            Ask Question
          </button>
        </div>
      </div>
      
      {/* Search and filters */}
      <div className="mb-6 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </form>
        
        <div className="flex items-center w-full md:w-auto">
          <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
            Sort by:
          </label>
          <select
            id="sort-order"
            value={sort}
            onChange={handleSortChange}
            className="block w-full md:w-auto pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="newest">Newest</option>
            <option value="popular">Most Upvoted</option>
            <option value="unanswered">Unanswered</option>
          </select>
        </div>
      </div>
      
      {/* Question Form */}
      {showQuestionForm && (
        <div className="mb-6 p-6 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Ask a Question for this Section
          </h3>
          <QuestionForm 
            onSubmit={handleQuestionSubmit} 
            onCancel={() => setShowQuestionForm(false)}
          />
        </div>
      )}
      
      {/* Questions List */}
      {filteredQuestions.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Questions Yet
          </h3>
          <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto">
            Be the first to ask a question about this specific section of {courseName} ({courseCode})
          </p>
          <button
            onClick={handleAskQuestion}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <Plus className="mr-2 -ml-1 h-5 w-5" />
            Ask Question
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredQuestions.map((question) => (
            <QuestionItem
              key={question.id}
              question={question}
              courseId={courseId}
              onVote={handleVote}
              onDelete={handleDeleteQuestion}
              user={user}
            />
          ))}
          
          {pagination.hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {loading ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-t-2 border-b-2 border-red-500 rounded-full animate-spin mr-2"></div>
                    Loading...
                  </span>
                ) : (
                  'Load More Questions'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionQuestionsTab;